import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useCallback, useRef, useState } from 'react';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';

interface Props {
  title: React.ReactNode;
  description: React.ReactNode;
  submitButtonTitle?: string;
  onSubmit: () => void;
  onClose: () => void;
  isOpen: boolean;
}

export function ConfirmationModal({
  title,
  description,
  submitButtonTitle = 'Confirm',
  onSubmit,
  onClose,
  isOpen,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const focusRef = useRef<HTMLButtonElement>(null);

  const onConfirm = useCallback(() => {
    try {
      setSubmitting(true);
      onSubmit();
      setSubmitting(false);
      onClose();
    } catch (e) {
      setSubmitting(false);
      setErrorMessage('There was an error submitting your request');
    }
  }, [onClose, onSubmit]);

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      onClose={onClose}
      initialFocusRef={focusRef}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          {description}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button
              variant="secondary"
              onClick={onClose}
              isDisabled={submitting}
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              isLoading={submitting}
              loadingText={submitButtonTitle}
              type="submit"
              ref={focusRef}
            >
              {submitButtonTitle}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
